/**
 * 重力加速度（studs/秒^2）。Roblox 既定の workspace.Gravity=196.2 に合わせる。
 * velocityY と World 生成の両方で使う。ジャンプ力（既定 50）と一組で跳躍高が決まる。
 */
export const GRAVITY = 196.2
/** Roblox Workspace.FallenPartsDestroyHeight の既定値（-500）。これより下のオブジェクトを除去する。 */
export const FALLEN_PARTS_DESTROY_HEIGHT = -500
/** 物理ステップの固定間隔（秒）。60fps 相当。 */
export const FIXED_STEP = 1 / 60
/** 1 フレームで処理する dt の上限（秒）。タブ復帰時の暴走を防ぐ。 */
export const MAX_FRAME_DT = 0.1
/** 文字コントローラの skin width（接触判定の安定化用ギャップ）。 */
export const CONTROLLER_OFFSET = 0.01
/** 足元 Touched 検知レイの向き（真下）。 */
export const GROUND_RAY_DIRECTION = { x: 0, y: -1, z: 0 }
/**
 * 足元 Touched 検知レイの余裕長（studs）。カプセル足裏から床面までの
 * snapToGround 追従誤差・skin width ぶんを吸収する。Touched 検知と、
 * autostep 持ち上げ中の接地救済（楔状態の脱出）に使う広い margin。
 */
export const GROUND_TOUCH_MARGIN = 0.3
/**
 * 接地判定レイの余裕長（studs）。足裏が実質床に着いているとみなす厳しい margin。
 * 広い margin を接地判定に常用すると床上 0.3 studs で浮いたまま落下が
 * 止まるため、通常の接地補完はこの値で判定する。
 */
export const GROUND_CONTACT_MARGIN = 0.06
/**
 * 台追従（applyCarry）の castShape sweep で障害物の手前に残す隙間（studs）。
 * 0 だと次ステップの KCC が初期接触扱いになり移動が乱れるため、わずかに空ける。
 */
export const CARRY_CONTACT_SKIN = 0.01
/** 台追従の castShape に渡すカプセルの向き（アバターは直立固定）。 */
export const CARRY_IDENTITY_ROTATION = { x: 0, y: 0, z: 0, w: 1 }

/**
 * 乗車プロンプト（「のる」）を出す座席までの最大距離（studs）。
 * rbxl の ProximityPrompt 'EndorsedVehicleProximityPromptV1' の
 * MaxActivationDistance=10 に合わせる。
 */
export const VEHICLE_PROMPT_DISTANCE = 10
/**
 * 車両の水平移動スイープで車体ボックスを縮める量（studs）。隣接する地面や
 * 既存接触のノイズで毎ステップ停止しないよう、わずかに小さい箱で掃引する。
 */
export const VEHICLE_SWEEP_SKIN = 0.1
/**
 * スイープ箱の下面を持ち上げる量（studs）。平地・緩い登り坂を「障害物」として
 * 誤検知して車が進めなくなるのを防ぐ（タイヤ半径 1.5 に対する床ぎわの余裕）。
 */
export const VEHICLE_SWEEP_GROUND_CLEAR = 0.6
/**
 * 接地スナップの許容距離（studs）。現在の車高（groundClearance）からこの範囲
 * 内に地面があれば吸着して走る（緩い起伏・下り坂への追従）。これを超えて地面が
 * 離れたら（段差から飛び出した等）重力落下に切り替える。
 */
export const VEHICLE_GROUND_SNAP_TOLERANCE = 2
/** 乗車時の車高計測レイの余裕長（studs）。AABB 半径より下の地面を探す。 */
export const VEHICLE_GROUND_PROBE_EXTRA = 10
/**
 * 着席時にアバター中心を座席へ沈める量（studs）。着席ポーズ（腿上げ・膝曲げ）で
 * 視覚上の身長が縮むぶん、立位の中心高（size.y/2）から下げて座面に合わせる。
 * 実機のスポーツカーは車高が低く、頭がルーフ下に収まる深さまで沈める
 * （RJF01 の実車との目視比較で調整した値）。
 */
export const VEHICLE_SEAT_SINK = 1.0
/**
 * 降車位置が塞がっていた場合のフォールバックで、車の屋根上に置く際の余裕
 * （studs）。実機の「シート上 +8 studs へ MoveTo」の簡略版。
 */
export const VEHICLE_EXIT_ROOF_MARGIN = 0.5
/**
 * 歩行で乗り越えられる段差の最大高さ（studs）。Roblox の Humanoid は
 * 1 stud 程度の段差（例: RJF01 の緑のゆか=厚み1の床の縁）をジャンプなしで
 * 登れるため、それに合わせる。壁（高さ数 studs 以上）はこの高さを超えるので
 * 従来どおり阻まれる。
 */
export const AUTOSTEP_MAX_HEIGHT = 1.2
/**
 * 自動段差登りに必要な踏み場の最小奥行き（studs）。
 * 注意: rapier の autostep はこの値が小さすぎる（例 0.1）とカプセル形状の
 * キャラクターで発火しない（実測）。0.5 なら段差 1.0 を安定して登れ、
 * maxHeight 超の段差・壁は引き続き阻まれることを確認済み。
 */
export const AUTOSTEP_MIN_WIDTH = 0.5
/**
 * 歩いて登れる最大斜度（ラジアン）。Roblox の Humanoid.MaxSlopeAngle 既定 89°
 * に合わせる。rapier KCC の既定は 45° で、RJF02 の色板の坂（45.3°）のような
 * 実機では歩けるわずかに急な斜面が登れなくなる。垂直の壁（90°）は従来どおり
 * 阻まれる。
 */
export const MAX_SLOPE_CLIMB_ANGLE = (89 * Math.PI) / 180
/**
 * 自動滑落を開始する斜度（ラジアン）。登れる斜度では滑り落ちないよう
 * MAX_SLOPE_CLIMB_ANGLE と同値にする（rapier 既定 45° のままだと 45.3° の
 * 坂で登った先から滑り落ちる）。
 */
export const MIN_SLOPE_SLIDE_ANGLE = MAX_SLOPE_CLIMB_ANGLE
/**
 * 簡易遊泳（terrain.water の水域内）のパラメータ。Roblox の Humanoid Swimming
 * を簡略化したもの: 浅瀬（足の水没がこの深さ未満）は海底を歩き、それより深いと
 * 遊泳になる。遊泳中は重力の代わりに浮力と抗力が働き、水面で自然に静止する。
 */
/** 遊泳へ切り替わる足元の水没深さ（studs）。腰の高さ相当。 */
export const SWIM_MIN_DEPTH = 2
/** 浮上の目標: アバター中心を水面からこの深さに保つ（頭が水面から出る）。 */
export const SWIM_SURFACE_CENTER_DEPTH = 1.2
/** ジャンプキー押下中の上昇速度（studs/秒）。 */
export const SWIM_UP_SPEED = 16
/**
 * 遊泳中にジャンプキーを押した瞬間の上昇初速（studs/秒）。陸上ジャンプ
 * （JumpPower=既定 50）より控えめで、水面から小さく跳び出して岸や低い
 * 足場へ上がれる程度（Roblox の水面ホップ相当）。
 */
export const SWIM_JUMP_SPEED = 35
/** 目標深度より沈んでいるときの浮力による上昇速度（studs/秒）。 */
export const SWIM_BUOYANCY_SPEED = 6
/** 水の抗力（1/秒）。入水時の落下の勢いを指数減衰させる。 */
export const SWIM_VERTICAL_DRAG = 5
/** 遊泳中の水平移動速度の倍率（歩行より遅い）。 */
export const SWIM_HORIZONTAL_SCALE = 0.7
/**
 * 水域（terrain.water）に浸かった動的オブジェクト（アンカー解除・クローン等）の
 * 浮力パラメータ。教材仕様「ボールは水に浮かせる」（RJF09）の実現で、
 * 材質・密度によらず一律に水面近くの平衡点へ浮上させる（アバターの簡易遊泳と
 * 同型の、速度ベースの近似）。実機 Roblox の浮力は材質密度に依存するが、
 * プレビューでは密度を再現せず「浮く」に揃える。
 */
/**
 * 浮遊平衡の深さ: 部品中心を水面からこの割合 × 部品の高さだけ沈んだ位置に保つ
 * （密度 0.7 の部品が浮くときの沈み方＝体積の 7 割が水没＝中心はおよそ
 * 0.2 × 高さぶん水面下、を見た目の基準にする）。
 */
export const OBJECT_FLOAT_CENTER_DEPTH_PER_HEIGHT = 0.2
/** 平衡点より沈んでいるときの浮力による上昇速度（studs/秒）。 */
export const OBJECT_FLOAT_BUOYANCY_SPEED = 6
/** 水中の抗力（rapier の linearDamping）。入水の勢い・水面の揺れを減衰させる。 */
export const OBJECT_WATER_LINEAR_DAMPING = 1.5
/** 陸上（既定）の linearDamping。動的化時の初期値かつ水から出たときの復帰値。 */
export const DYNAMIC_LINEAR_DAMPING = 0.05
/**
 * 滑り台の滑走パラメータ（RJF02）。「すべり台」という名前のオブジェクトの
 * 斜面に乗ると、操作不能で倒れて滑り降りる（実機のすべり台ギミックの再現）。
 * 滑走方向は接地面の法線から求めた最大勾配の下り方向。台から出た後は摩擦で
 * 減速し、十分遅くなったら通常操作へ戻る。
 */
/** 滑走対象のオブジェクト名。 */
export const SLIDE_OBJECT_NAME = 'すべり台'
/** 滑走の加速度（studs/s²）。重力の斜面成分（45° で g·sin45°≒139）より緩め。 */
export const SLIDE_ACCELERATION = 40
/** 滑走の最高速度（studs/s）。 */
export const SLIDE_MAX_SPEED = 40
/**
 * 台から出た後の減速度（studs/s²）。RJF02 では滑り台の先にボウリングピンが
 * 並んでおり、滑ってきた勢いのままピンへ突っ込んで倒すのが本家の体験
 * （最高速 40 からおよそ 40 studs 滑走して止まる）。
 */
export const SLIDE_FRICTION = 20
/** 滑走をやめる速度（studs/s）。これ未満まで減速したら通常操作へ戻る。 */
export const SLIDE_EXIT_SPEED = 6
/**
 * WalkSpeed（プロパティ値）→ 実移動速度への倍率。プレビューのアバターは実機の
 * R15 キャラクターより小柄・小回りなため、同じ WalkSpeed 値でも体感が遅く、
 * RJF08 等で教材通りの値ではギミックを越えられなかった。値そのものは Roblox の
 * プロパティと一致させたまま（reporter やブロック設定値を歪めない）、実速度
 * だけを 1.2 倍して実機の到達距離に合わせる。
 */
export const WALK_SPEED_SCALE = 1.2
/**
 * JumpPower（プロパティ値）→ ジャンプ初速への倍率。実機では JumpPower=50 で
 * 飛び移れる足場が、プレビューでは 60 が必要だった（100 の壁越えも 110〜120 が
 * 必要）。WALK_SPEED_SCALE と同じ理由・同じ倍率で初速のみ補正し、教材記載の
 * 値で実機と同じ跳躍ができるようにする。
 */
export const JUMP_POWER_SCALE = 1.2
/**
 * 死亡（体力 0・奈落落下）からリスポーンまでの待ち時間（秒）。
 * Roblox の Players.RespawnTime 既定 3 秒に合わせ、「何が起きてやられたか」を
 * プレイヤーが視認できる間を作る。
 */
export const RESPAWN_DELAY_SECONDS = 3
